import { useQuery } from "@tanstack/react-query";
import type { Invitation } from "@zworks/console-contracts";
import * as React from "react";
import { useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { ApiError, acceptInvitation, invitationQuery } from "@/lib/api";
import { formatDate } from "@/lib/format";

const roleLabels: Record<Invitation["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

type BadLinkKind = "expired" | "accepted" | "revoked";

/** True when the preview failed because nobody is signed in, not because the link is bad. */
function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function kindFromStatus(status: Invitation["status"]): BadLinkKind | null {
  switch (status) {
    case "expired":
      return "expired";
    case "accepted":
      return "accepted";
    case "revoked":
      return "revoked";
    default:
      return null;
  }
}

/**
 * The backend answers each bad link with its own problem, not a generic 400, so the page maps
 * the problem text to the matching state. A title that names expiry, revocation, or a second
 * accept lands on the right copy and the one next action.
 */
function kindFromError(error: unknown): BadLinkKind | null {
  if (error instanceof ApiError) {
    const text =
      `${error.problem?.title ?? ""} ${error.problem?.detail ?? ""} ${error.message}`.toLowerCase();
    if (text.includes("expir")) return "expired";
    if (text.includes("revok")) return "revoked";
    if (text.includes("already")) return "accepted";
  }
  return null;
}

function projectInitials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  const letters = words
    .slice(0, 2)
    .map((word) => word[0])
    .join("");
  return (letters || name.slice(0, 2)).toUpperCase();
}

function requestNewInvitation(projectName: string | null) {
  const body = projectName
    ? `Please send me a new invitation to join ${projectName} in Zworks.`
    : "Please send me a new invitation to join the project in Zworks.";
  window.location.href = `mailto:?subject=${encodeURIComponent("New Zworks invitation")}&body=${encodeURIComponent(body)}`;
}

function contactOwner(projectName: string | null) {
  const body = projectName
    ? `My invitation to join ${projectName} was revoked. Please let me know what to do next.`
    : "My invitation was revoked. Please let me know what to do next.";
  window.location.href = `mailto:?subject=${encodeURIComponent("Zworks invitation revoked")}&body=${encodeURIComponent(body)}`;
}

function openProject() {
  window.location.href = "/";
}

/**
 * Signed out with an invitation link. The link stays usable: after signing in, the person lands
 * back on it. Signing in creates the account on first use, so this is also the invited person's
 * first step.
 */
function SignInNeeded({ token, onRetry }: { token: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Badge variant="info">Sign in needed</Badge>
      <EmptyState
        title="Sign in to see this invitation"
        description="Sign in to see this invitation, then accept it."
        action={{
          label: "Sign in",
          onClick: () =>
            (window.location.href = `/login?returnTo=${encodeURIComponent(`/invitations/${token}`)}`),
        }}
      />
      <Button variant="ghost" size="sm" type="button" onClick={onRetry}>
        I already signed in
      </Button>
    </div>
  );
}

/**
 * The invitation accept page, as the InviteAccept board draws it. The token travels only in the
 * link and is never rendered. A pending invitation shows the project, the invited role, and who
 * invited, with one primary action. Each bad link keeps its own labelled state with one next
 * action, and status is always a labelled badge, never colour alone.
 */
export function InvitationAcceptPage() {
  const { token } = useParams();
  const invitation = useQuery({ ...invitationQuery(token ?? ""), enabled: Boolean(token) });
  const [accepting, setAccepting] = React.useState(false);
  const [accepted, setAccepted] = React.useState<Invitation | null>(null);
  const [acceptError, setAcceptError] = React.useState<ApiError | null>(null);

  async function onAccept() {
    if (!token || accepting) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      setAccepted(await acceptInvitation(token));
    } catch (error) {
      setAcceptError(
        error instanceof ApiError
          ? error
          : new ApiError("The request could not be completed.", 500),
      );
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col gap-4 py-6">
          {!token ? (
            <ErrorState
              title="This invitation link is not valid"
              description="The link is missing its token. Ask the project owner for a new invitation."
              requestId="unknown"
              onRetry={() => window.location.reload()}
            />
          ) : accepted ? (
            <AcceptedState invitation={accepted} fresh />
          ) : acceptError && kindFromError(acceptError) === "accepted" ? (
            <AcceptedState
              invitation={invitation.data ?? null}
              projectName={invitation.data?.projectName ?? null}
              role={invitation.data?.role ?? null}
            />
          ) : acceptError && kindFromError(acceptError) === "expired" ? (
            <ExpiredState projectName={invitation.data?.projectName ?? null} />
          ) : acceptError && kindFromError(acceptError) === "revoked" ? (
            <RevokedState projectName={invitation.data?.projectName ?? null} />
          ) : acceptError ? (
            <ErrorState
              title="This invitation could not be accepted"
              description={
                acceptError.problem?.detail ??
                acceptError.message ??
                "The request could not be completed."
              }
              requestId={acceptError.requestId ?? "unknown"}
              onRetry={onAccept}
            />
          ) : invitation.isPending ? (
            <LoadingState label="Loading invitation" rows={3} />
          ) : invitation.isError && isUnauthenticated(invitation.error) ? (
            <SignInNeeded token={token ?? ""} onRetry={() => void invitation.refetch()} />
          ) : invitation.isSuccess && invitation.data.status === "pending" ? (
            <PendingInvitation
              invitation={invitation.data}
              accepting={accepting}
              onAccept={onAccept}
            />
          ) : invitation.isSuccess ? (
            <InvitationByStatus invitation={invitation.data} />
          ) : (
            <InvitationLoadError
              token={token ?? ""}
              error={invitation.error}
              onRetry={() => void invitation.refetch()}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PendingInvitation({
  invitation,
  accepting,
  onAccept,
}: {
  invitation: Invitation;
  accepting: boolean;
  onAccept: () => void;
}) {
  const role = roleLabels[invitation.role];
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span
        aria-hidden
        className="grid size-12 place-items-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground"
      >
        {projectInitials(invitation.projectName)}
      </span>
      <div className="flex flex-col items-center gap-2">
        <Badge variant="secondary">Invitation pending</Badge>
        <h1 className="max-w-full text-[1.75rem] leading-tight font-semibold tracking-tight [overflow-wrap:anywhere]">
          Join {invitation.projectName}
        </h1>
        <p className="text-sm text-muted-foreground">
          <strong className="font-semibold text-foreground">{invitation.invitedBy}</strong> invited
          you as a <strong className="font-semibold text-foreground">{role}</strong>.
        </p>
      </div>
      <ul className="flex w-full flex-col gap-2 text-left text-sm text-muted-foreground">
        <li>Start and follow runs, edit flows, and work with the team&apos;s work items.</li>
        <li>Use the project&apos;s credits. You do not pay and you cannot buy credits.</li>
        <li>Billing, members, and credentials stay with the project owner.</li>
      </ul>
      <div className="flex w-full flex-col gap-2">
        <Button size="lg" className="w-full" type="button" disabled={accepting} onClick={onAccept}>
          {accepting ? "Accepting…" : "Accept invitation"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Expires {formatDate(invitation.expiresAt)}.</p>
    </div>
  );
}

function AcceptedState({
  invitation,
  projectName,
  role,
  fresh = false,
}: {
  invitation: Invitation | null;
  projectName?: string | null;
  role?: Invitation["role"] | null;
  fresh?: boolean;
}) {
  const name = invitation?.projectName ?? projectName ?? "the project";
  const roleLabel = invitation ? roleLabels[invitation.role] : role ? roleLabels[role] : null;
  const title = fresh ? `You joined ${name}` : "You are already a member";
  const description =
    fresh && roleLabel
      ? `You are in ${name} as a ${roleLabel}.`
      : fresh
        ? `You are in ${name}.`
        : `You joined ${name} before. The membership already exists.`;
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Badge variant="success">Accepted</Badge>
      <EmptyState
        title={title}
        description={description}
        action={{ label: "Open the project", onClick: openProject }}
      />
    </div>
  );
}

function ExpiredState({ projectName }: { projectName: string | null }) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Badge variant="warning">Expired</Badge>
      <EmptyState
        title="This invitation has expired"
        description={
          projectName
            ? `The invitation to join ${projectName} has expired. Ask the owner for a new one. Nothing was changed.`
            : "This invitation has expired. Ask the owner for a new one. Nothing was changed."
        }
        action={{
          label: "Request a new invitation",
          onClick: () => requestNewInvitation(projectName),
        }}
      />
    </div>
  );
}

function RevokedState({ projectName }: { projectName: string | null }) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Badge variant="danger">Revoked</Badge>
      <EmptyState
        title="This invitation was revoked"
        description={
          projectName
            ? `The owner of ${projectName} revoked this invitation. Nothing was changed.`
            : "The project owner revoked this invitation. Nothing was changed."
        }
        action={{
          label: "Contact the project owner",
          onClick: () => contactOwner(projectName),
        }}
      />
    </div>
  );
}

function InvitationByStatus({ invitation }: { invitation: Invitation }) {
  switch (kindFromStatus(invitation.status)) {
    case "expired":
      return <ExpiredState projectName={invitation.projectName} />;
    case "revoked":
      return <RevokedState projectName={invitation.projectName} />;
    case "accepted":
      return <AcceptedState invitation={invitation} />;
    default:
      return <ExpiredState projectName={invitation.projectName} />;
  }
}

function InvitationLoadError({
  token,
  error,
  onRetry,
}: {
  token: string;
  error: unknown;
  onRetry: () => void;
}) {
  const kind = kindFromError(error);
  if (kind === "expired") return <ExpiredState projectName={null} />;
  if (kind === "revoked") return <RevokedState projectName={null} />;
  if (kind === "accepted")
    return <AcceptedState invitation={null} projectName={null} role={null} />;
  if (isUnauthenticated(error)) return <SignInNeeded token={token} onRetry={onRetry} />;
  if (error instanceof ApiError) {
    return (
      <ErrorState
        title="This invitation link is not valid"
        description={error.problem?.detail ?? error.message ?? "No resource exists at this path."}
        requestId={error.requestId ?? "unknown"}
        onRetry={onRetry}
      />
    );
  }
  return (
    <ErrorState
      title="This invitation link is not valid"
      description="No resource exists at this path."
      requestId="unknown"
      onRetry={onRetry}
    />
  );
}
